import api from './api';

export const flowService = {
    // Listar todos os fluxos
    list: async () => {
        const response = await api.get('/flows-new');
        
        // Garante que todos os fluxos tenham a propriedade available definida como true por padrão
        if (response.data && Array.isArray(response.data)) {
            response.data = response.data.map(flow => ({
                ...flow,
                available: flow.available === undefined ? true : flow.available
            }));
        }
        
        return response.data;
    },

    // Obter um fluxo específico
    get: async (id) => {
        // Se o id for 'new', retorna um fluxo vazio
        if (id === 'new') {
            return {
                id: 0,
                name: '',
                is_active: true,
                available: true,
                questions: [],
                created_at: new Date().toISOString(),
            };
        }
        
        // Verifica se o ID é válido
        if (id === 'undefined' || id === 'NaN' || id === undefined || id === null) {
            console.log('ID inválido, retornando fluxo vazio');
            return {
                id: 0,
                name: '',
                is_active: true,
                available: true,
                questions: [],
                created_at: new Date().toISOString(),
            };
        }
        
        try {
            console.log(`Obtendo fluxo com ID: ${id}`);
            const response = await api.get(`/flows-new/${id}`);
            
            // Garante que a propriedade available esteja definida
            if (response.data) {
                response.data.available = response.data.available === undefined ? true : response.data.available;
            }
            
            // Garante que o next_question_logic é um objeto para cada questão
            if (response.data && response.data.questions) {
                console.log(`Fluxo obtido com ${response.data.questions.length} questões`);
                
                // Garante que o nome do fluxo existe
                if (!response.data.name) {
                    console.warn('Nome do fluxo não encontrado na resposta do backend, usando nome armazenado localmente');
                    // Tenta recuperar o nome do fluxo do localStorage
                    const storedFlowName = localStorage.getItem(`flow_${id}_name`);
                    if (storedFlowName) {
                        response.data.name = storedFlowName;
                    } else {
                        response.data.name = 'Fluxo sem nome';
                    }
                } else {
                    // Armazena o nome do fluxo no localStorage para uso futuro
                    localStorage.setItem(`flow_${id}_name`, response.data.name);
                }
                
                // Recupera o mapeamento atual de IDs
                const idMappingStr = localStorage.getItem(`flow_${id}_id_mapping`);
                const idMapping = idMappingStr ? JSON.parse(idMappingStr) : {};
                
                // Recupera o histórico de mapeamentos de IDs
                const idMappingHistoryStr = localStorage.getItem(`flow_${id}_id_mapping_history`);
                const idMappingHistory = idMappingHistoryStr ? JSON.parse(idMappingHistoryStr) : [];
                
                // Cria um novo mapeamento para esta versão
                const currentMapping = {};
                
                // Filtra questões inválidas
                response.data.questions = response.data.questions.filter(question => 
                    question && question.id && question.text
                );
                
                console.log(`Após filtrar questões inválidas: ${response.data.questions.length} questões`);
                
                // Armazena o mapeamento de IDs para uso futuro
                response.data.questions.forEach(question => {
                    // Armazena o ID original da questão
                    // Se já temos um mapeamento para este ID, usamos o ID original do mapeamento
                    if (idMapping[question.id]) {
                        question.original_id = idMapping[question.id];
                    } else {
                        question.original_id = question.id;
                    }
                    
                    // Registra o mapeamento
                    currentMapping[question.original_id] = question.id;
                    
                    // Garante que next_question_logic é um objeto
                    if (!question.next_question_logic) {
                        question.next_question_logic = {};
                    } else if (typeof question.next_question_logic === 'string') {
                        try {
                            question.next_question_logic = JSON.parse(question.next_question_logic);
                        } catch (e) {
                            console.error('Erro ao parsear next_question_logic:', e);
                            question.next_question_logic = {};
                        }
                    }
                    
                    // Garante que options é um array
                    if (!question.options) {
                        question.options = ["Sim", "Não"];
                    } else if (typeof question.options === 'string') {
                        try {
                            const parsedOptions = JSON.parse(question.options);
                            question.options = Array.isArray(parsedOptions) ? parsedOptions : [question.options];
                        } catch (e) {
                            console.error('Erro ao parsear options:', e);
                            question.options = ["Sim", "Não"];
                        }
                    }
                    
                    // Garante que is_active é true
                    question.is_active = true;
                });
                
                // Adiciona o mapeamento atual ao histórico
                idMappingHistory.push(currentMapping);
                
                // Limita o histórico a 10 entradas para não sobrecarregar o localStorage
                if (idMappingHistory.length > 10) {
                    idMappingHistory.shift();
                }
                
                // Armazena o histórico de mapeamentos no localStorage
                localStorage.setItem(`flow_${id}_id_mapping_history`, JSON.stringify(idMappingHistory));
                console.log('Histórico de mapeamentos de IDs atualizado:', idMappingHistory);
                
                // Armazena o mapeamento atual no localStorage
                localStorage.setItem(`flow_${id}_id_mapping`, JSON.stringify(currentMapping));
                console.log('Mapeamento de IDs atual armazenado:', currentMapping);
            }
            
            return response.data;
        } catch (error) {
            console.error('Erro ao obter fluxo:', error);
            throw error;
        }
    },

    // Criar um novo fluxo
    create: async (flow) => {
        // Verifica se o fluxo tem um nome válido
        if (!flow.name || !flow.name.trim()) {
            console.error('Tentativa de criar fluxo sem nome');
            throw new Error('O fluxo precisa ter um nome');
        }
        
        // Cria um mapa de IDs antigos para novos IDs temporários
        const idMapping = {};
        
        // Garante que o next_question_logic é um objeto para cada questão
        if (flow.questions) {
            // Primeiro, cria IDs temporários para todas as questões
            flow.questions = flow.questions.map((question, index) => {
                const oldId = question.id;
                const newId = Date.now() + index; // Cria um ID temporário único
                idMapping[oldId] = newId;
                
                // Converte options diretamente para JSON string
                let optionsArray = ["Sim", "Não"];
                if (question.options) {
                    if (Array.isArray(question.options)) {
                        optionsArray = question.options;
                    } else if (typeof question.options === 'string') {
                        try {
                            // Tenta parsear como JSON primeiro
                            const parsed = JSON.parse(question.options);
                            optionsArray = Array.isArray(parsed) ? parsed : [question.options];
                        } catch {
                            // Se falhar, trata como string simples
                            optionsArray = [question.options];
                        }
                    }
                }
                question.options = JSON.stringify(optionsArray);
                
                // Garante que os campos de texto estejam definidos e sem espaços extras
                if (question.text) {
                    question.text = question.text.trim();
                }
                
                if (question.final_message) {
                    question.final_message = question.final_message.trim();
                } else if (question.is_final) {
                    question.final_message = '';
                }
                
                if (question.article_reference) {
                    question.article_reference = question.article_reference.trim();
                } else {
                    question.article_reference = '';
                }
                
                return {
                    ...question,
                    id: newId,
                    flow_id: undefined,
                    original_id: oldId
                };
            });
            
            // Depois, atualiza o next_question_logic de cada questão
            flow.questions = flow.questions.map(question => {
                if (!question.next_question_logic) {
                    question.next_question_logic = {};
                }
                
                // Se for uma string JSON, converte para objeto
                if (typeof question.next_question_logic === 'string') {
                    try {
                        question.next_question_logic = JSON.parse(question.next_question_logic);
                    } catch (e) {
                        console.error('Erro ao parsear next_question_logic:', e);
                        question.next_question_logic = {};
                    }
                }

                // Atualiza os IDs no next_question_logic
                const updatedLogic = {};
                Object.entries(question.next_question_logic).forEach(([key, value]) => {
                    // Se o valor existe no mapeamento, usa o novo ID
                    updatedLogic[key] = idMapping[value] || value;
                });

                console.log(`Atualizando next_question_logic para questão ${question.id}:`, {
                    antigo: question.next_question_logic,
                    novo: updatedLogic
                });

                return {
                    ...question,
                    next_question_logic: updatedLogic
                };
            });

            // Atualiza o first_question_id se necessário
            if (flow.first_question_id) {
                flow.first_question_id = idMapping[flow.first_question_id] || flow.first_question_id;
            }
        }
        
        try {
            console.log('Criando novo fluxo:', flow);
            const response = await api.post('/flows-new', flow);
            
            // Armazena o mapeamento de IDs no localStorage para o novo fluxo
            if (response.data.id) {
                localStorage.setItem(`flow_${response.data.id}_id_mapping`, JSON.stringify(idMapping));
                localStorage.setItem(`flow_${response.data.id}_id_mapping_history`, JSON.stringify([idMapping]));
                
                // Armazena o nome do fluxo no localStorage
                if (response.data.name) {
                    localStorage.setItem(`flow_${response.data.id}_name`, response.data.name);
                } else if (flow.name) {
                    localStorage.setItem(`flow_${response.data.id}_name`, flow.name);
                    // Garante que o nome está na resposta
                    response.data.name = flow.name;
                }
            }
            
            return response.data;
        } catch (error) {
            console.error('Erro ao criar fluxo:', error);
            throw error;
        }
    },

    // Atualizar um fluxo existente
    update: async (id, flow) => {
        try {
            // Verifica se o ID é válido
            if (isNaN(id) || id === 'NaN') {
                console.error('ID de fluxo inválido:', id);
                throw new Error('ID de fluxo inválido. Por favor, tente novamente.');
            }
            
            console.log(`Atualizando fluxo ${id}:`, flow);
            
            // Armazena o nome do fluxo no localStorage para recuperação futura
            if (flow.name) {
                localStorage.setItem(`flow_${id}_name`, flow.name);
            }
            
            // Recupera o mapeamento atual de IDs
            const idMappingStr = localStorage.getItem(`flow_${id}_id_mapping`);
            const idMapping = idMappingStr ? JSON.parse(idMappingStr) : {};
            console.log('Mapeamento de IDs atual recuperado:', idMapping);
            
            // Verifica se estamos apenas atualizando campos simples como available
            if (!flow.questions) {
                console.log('Atualizando apenas campos simples do fluxo, sem questões');
                const response = await api.put(`/flows-new/${id}`, flow);
                return response.data;
            }
            
            // Cria um novo mapeamento para esta atualização
            const newIdMapping = {};
            
            // Garante que cada questão tenha um ID único
            // Isso evita a sobrescrita de questões existentes
            flow.questions = flow.questions.map(question => {
                // Se a questão já tem um ID, mantém o mesmo ID
                if (question.id) {
                    // Registra o ID no mapeamento
                    if (question.original_id) {
                        newIdMapping[question.original_id] = question.id;
                    } else {
                        newIdMapping[question.id] = question.id;
                    }
                    return question;
                }
                
                // Se é uma nova questão, gera um ID único
                const newId = Date.now() + Math.floor(Math.random() * 1000);
                newIdMapping[newId] = newId;
                
                return {
                    ...question,
                    id: newId,
                    original_id: newId
                };
            });
            
            // Processa as questões antes de enviar ao backend
            const processedQuestions = flow.questions.map(question => {
                // Garante que next_question_logic seja um objeto
                if (!question.next_question_logic) {
                    question.next_question_logic = {};
                }
                
                // Converte options diretamente para JSON string
                let optionsArray = ["Sim", "Não"];
                if (question.options) {
                    if (Array.isArray(question.options)) {
                        optionsArray = question.options;
                    } else if (typeof question.options === 'string') {
                        try {
                            // Tenta parsear como JSON primeiro
                            const parsed = JSON.parse(question.options);
                            optionsArray = Array.isArray(parsed) ? parsed : [question.options];
                        } catch {
                            // Se falhar, trata como string simples
                            optionsArray = [question.options];
                        }
                    }
                }
                question.options = JSON.stringify(optionsArray);
                
                // Garante que os campos de texto estejam definidos e sem espaços extras
                if (question.text) {
                    question.text = question.text.trim();
                }
                
                if (question.final_message) {
                    question.final_message = question.final_message.trim();
                } else if (question.is_final) {
                    question.final_message = '';
                }
                
                if (question.article_reference) {
                    question.article_reference = question.article_reference.trim();
                } else {
                    question.article_reference = '';
                }
                
                // Processa o campo is_success para questões finais
                if (question.is_final) {
                    if (question.is_success === true) {
                        question.is_success = true;
                    } else if (question.is_success === false) {
                        question.is_success = false;
                    } else {
                        question.is_success = null;
                    }
                }
                
                // Limpa o parent_id para evitar problemas de chave estrangeira
                question.parent_id = null;
                
                // Garante que a questão tenha o flow_id correto
                question.flow_id = id;
                
                // Garante que a questão tenha o campo is_active definido como true
                question.is_active = true;
                
                return question;
            });
            
            // Atualiza o fluxo com as questões processadas
            const processedFlow = {
                ...flow,
                questions: processedQuestions
            };
            
            // Atualiza o histórico de mapeamentos
            const idMappingHistoryStr = localStorage.getItem(`flow_${id}_id_mapping_history`);
            const idMappingHistory = idMappingHistoryStr ? JSON.parse(idMappingHistoryStr) : [];
            idMappingHistory.push(newIdMapping);
            
            // Limita o histórico a 10 entradas
            if (idMappingHistory.length > 10) {
                idMappingHistory.shift();
            }
            
            console.log('Histórico de mapeamentos de IDs atualizado:', idMappingHistory);
            localStorage.setItem(`flow_${id}_id_mapping_history`, JSON.stringify(idMappingHistory));
            
            // Atualiza o mapeamento atual
            console.log('Novo mapeamento de IDs armazenado:', newIdMapping);
            localStorage.setItem(`flow_${id}_id_mapping`, JSON.stringify(newIdMapping));
            
            // Cria uma cópia profunda do objeto para evitar problemas de referência
            const flowToSend = JSON.parse(JSON.stringify(processedFlow));
            
            console.log('Dados finais a serem enviados ao backend:', flowToSend);
            
            // Envia para o backend
            const response = await api.put(`/flows-new/${id}`, flowToSend);
            
            // Verifica se a resposta contém todas as questões
            if (response.data && response.data.questions) {
                console.log(`Resposta do backend contém ${response.data.questions.length} questões`);
                
                // Verifica se todas as questões enviadas estão na resposta
                const sentQuestionIds = new Set(processedQuestions.map(q => q.id.toString()));
                const receivedQuestionIds = new Set(response.data.questions.map(q => q.id.toString()));
                
                console.log('IDs de questões enviadas:', Array.from(sentQuestionIds));
                console.log('IDs de questões recebidas:', Array.from(receivedQuestionIds));
                
                // Verifica se há questões faltando
                const missingQuestions = [];
                sentQuestionIds.forEach(id => {
                    if (!receivedQuestionIds.has(id)) {
                        missingQuestions.push(id);
                    }
                });
                
                if (missingQuestions.length > 0) {
                    console.warn('Questões faltando na resposta:', missingQuestions);
                    
                    // Tenta recuperar as questões faltando
                    const missingQuestionsData = processedQuestions.filter(q => 
                        missingQuestions.includes(q.id.toString())
                    );
                    
                    // Adiciona as questões faltando à resposta
                    response.data.questions = [
                        ...response.data.questions,
                        ...missingQuestionsData
                    ];
                    
                    console.log('Questões adicionadas manualmente à resposta:', missingQuestionsData);
                }
            }
            
            return response.data;
        } catch (error) {
            console.error('Erro ao atualizar fluxo:', error);
            throw error;
        }
    },

    // Excluir (desativar) um fluxo
    delete: async (id) => {
        const response = await api.delete(`/flows-new/${id}`);
        // Remove o mapeamento de IDs e o histórico do localStorage
        localStorage.removeItem(`flow_${id}_id_mapping`);
        localStorage.removeItem(`flow_${id}_id_mapping_history`);
        return response.data;
    },

    // Atualizar uma questão específica
    updateQuestion: async (flowId, questionId, questionData) => {
        try {
            // Verifica se o ID do fluxo é válido
            if (isNaN(flowId) || flowId === 'NaN' || flowId === 'undefined' || flowId === null) {
                console.error('ID de fluxo inválido:', flowId);
                throw new Error('ID de fluxo inválido. Por favor, tente novamente.');
            }
            
            // Verifica se o ID da questão é válido
            if (isNaN(questionId) || questionId === 'NaN' || questionId === 'undefined' || questionId === null) {
                console.error('ID de questão inválido:', questionId);
                throw new Error('ID de questão inválido. Por favor, tente novamente.');
            }
            
            console.log(`Atualizando questão ${questionId} do fluxo ${flowId}:`, questionData);
            
            // Processa os dados antes de enviar
            const processedData = {
                ...questionData,
                text: questionData.text?.trim(),
                final_message: questionData.final_message?.trim() || '',
                article_reference: questionData.article_reference?.trim() || '',
            };
            
            // Processa o campo options para garantir que seja JSON
            if (Array.isArray(processedData.options)) {
                processedData.options = JSON.stringify(processedData.options);
            } else if (typeof processedData.options === 'string') {
                try {
                    // Verifica se já é uma string JSON válida
                    JSON.parse(processedData.options);
                } catch (e) {
                    // Se não for um JSON válido, tenta converter para array e depois para JSON
                    try {
                        const optionsArray = Array.isArray(processedData.options) ? processedData.options : [processedData.options];
                        processedData.options = JSON.stringify(optionsArray);
                    } catch (err) {
                        // Se falhar, define um valor padrão
                        processedData.options = JSON.stringify(['Sim', 'Não']);
                    }
                }
            } else {
                // Se não for nem array nem string, define um valor padrão
                processedData.options = JSON.stringify(['Sim', 'Não']);
            }
            
            // Se for uma questão final, processa o campo is_success
            if (questionData.is_final) {
                if (questionData.is_success === true) {
                    processedData.is_success = true;
                } else if (questionData.is_success === false) {
                    processedData.is_success = false;
                } else {
                    processedData.is_success = null;
                }
            }
            
            const response = await api.put(`/flows-new/${flowId}/questions/${questionId}`, processedData);
            return response.data;
        } catch (error) {
            console.error('Erro ao atualizar questão:', error);
            throw error;
        }
    }
}; 